import { SyncIcon } from "@fluentui/react-icons-mdl2"
import { ActionButton } from "@fluentui/react/lib/Button"
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner"
import { Stack } from "@fluentui/react/lib/Stack"
import {
    getTheme,
    IPartialTheme,
    IProcessedStyleSet,
    registerIcons
} from "@fluentui/react/lib/Styling"
import { Text } from "@fluentui/react/lib/Text"
import { classNamesFunction } from "@fluentui/react/lib/Utilities"
import { Bus } from "baconjs"
import { merge } from "lodash"
import React, { Dispatch, SetStateAction, useEffect, useState } from "react"

import { ConfigurationService } from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"
import * as reactConfiguratorImport from "@encoway/react-configurator"
import { ComponentFactory } from "@encoway/react-configurator"
import { Visualization as VisualizationProvider } from "@encoway/visual-editor"

import { IConfigurationSettings } from "../../configurationService.types"
import {
    BorderLayoutContent,
    ComponentName,
    Events,
    ExternalMessages,
    ReadyState
} from "../../constants"
import { postMessage, registerForExternalMessage } from "../../messageUtil"
import { VisualizationService } from "../../services/visualization/visualizationService"
import {
    IVisualizationEditorSettings,
    IVisualizationNode,
    IVisualizationProvider,
    IVisualizationServer,
    IVisualizationServiceSettings
} from "../../services/visualization/visualizationService.types"
import {
    ICfg,
    ISaveData,
    ISaveResult,
    IVisualizationWrapperProps,
    IVisualizationWrapperStyles,
    VisualizationDataSaveEvents
} from "./VisualizationWrapper.types"

export {
    IVisualizationWrapperStyles,
    ISaveData,
    VisualizationDataSaveEvents,
    ISaveResult,
    IVisualizationWrapperProps
} from "./VisualizationWrapper.types"

const SYNC = "Sync"
registerIcons(
    {
        icons: {
            [SYNC]: <SyncIcon />
        }
    },
    { disableWarnings: true }
)

interface IAppStateSettings extends IVisualizationServiceSettings {
    theme?: IPartialTheme
}

interface IAppState {
    initialized: boolean
    visualizationEnabled: boolean
    settings: IAppStateSettings
    editorSettings: IVisualizationEditorSettings
    theme?: IPartialTheme
    configurationSettings: IConfigurationSettings
    configurationService?: ConfigurationService
    visualization?: IVisualizationServer
    node: IVisualizationNode
    visualizationProvider: IVisualizationProvider
}

export const getVisualizationProvider = () => VisualizationProvider

export const VisualizationWrapper = (props: IVisualizationWrapperProps) => {
    const {
        styles,
        theme,
        settings,
        eventBus,
        configurationSettings,
        configurationService,
        visualizationProvider,
        visualizationState,
        visualEditorRef,
        reactConfigurator,
        setBusyOverlay,
        ...delegatedProps
    } = props
    const classNames =
        props.classNames ||
        (classNamesFunction()(
            styles,
            theme
        ) as IProcessedStyleSet<IVisualizationWrapperStyles>)
    const [state, setState] = useState({
        initialized: false,
        visualizationEnabled: isVisualizationEnabled(settings),
        visualizationProvider:
            visualizationProvider || getVisualizationProvider(),
        settings: settings as IAppStateSettings,
        editorSettings: settings?.settings,
        configurationSettings,
        configurationService
    } as IAppState)

    const [loadState, setLoadState] = useState({
        loading: false,
        failure: false
    } as ILoadStatus)

    const [errorMessage, setErrorMessage] = useState(null)
    const myReactConfigurator = reactConfigurator || reactConfiguratorImport

    const load: (
        currentState: IAppState,
        unsubscribes: Unsubscribes
    ) => void = async (currentState, unsubscribes) => {
        loadVisualization(currentState, visualizationState)
            .then((appState: IAppState) => {
                if (appState.visualization) {
                    VisualizationService.init(appState.visualization, eventBus)
                    VisualizationService.setupVisualizationOptions(
                        state.configurationSettings.options.articleName,
                        appState.settings
                    )
                }

                setState(appState)
                setLoadState({ loading: false, failure: false })
                if (appState.visualization && appState.node) {
                    unsubscribes.push(
                        subscribeConfigurationState(
                            eventBus,
                            appState,
                            myReactConfigurator
                        )
                    )
                }
                if (appState.visualization) {
                    unsubscribes.push(
                        () => appState.visualization?.cloud?.clear(),
                        subscribeErrorMessages(eventBus, setErrorMessage)
                    )
                }
            })
            .catch((e) => {
                console.error("Failed to load visualization", e)
                setLoadState({ loading: false, failure: true })
            })
    }

    const loadVis = () => {
        const unsubscribes: Unsubscribes = []
        const loadFunc = async () => {
            load(state, unsubscribes)
        }
        loadFunc().then()
        return () => unsubscribes.forEach((u) => u())
    }

    useEffect(() => {
        return loadVis()
    }, [])

    useEffect(() => {
        if (!state.visualization && loadState.loading) {
            return loadVis()
        }
    }, [loadState])

    const generateSaveData = async () => {
        setBusyOverlay && setBusyOverlay(true)
        let error
        let renderSettings
        try {
            renderSettings = await VisualizationService.determineRenderSettings(
                state.configurationSettings.options.articleName,
                state.settings
            )
        } catch (e) {
            console.warn(e)
            error = "Application.Visualization.Render.Settings.failure"
        }

        let data: ISaveData = {}
        if (renderSettings !== undefined) {
            try {
                data = {
                    images: await VisualizationService.render(renderSettings)
                }
            } catch (e) {
                console.warn(e)
                error = "Application.Visualization.Render.failure"
            }
        }
        if (state.settings.mode === "assembly" && state.visualization?.cloud) {
            const cloud = state.visualization.cloud
            try {
                const resultBomType =
                    state.editorSettings.assembly?.resultBomType
                let useType
                if (resultBomType) {
                    useType = cloud.bomExportTypes?.find(
                        (t) => resultBomType === t
                    )
                    if (!useType) {
                        console.warn(
                            "specified result bom type '" +
                                resultBomType +
                                "' not supported",
                            cloud.bomExportTypes
                        )
                    }
                }
                const assemblyState = await cloud.result(useType)
                if (assemblyState) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { state, ...other } = assemblyState
                    const readyState =
                        VisualizationService.determineConfigurationReadyState(
                            cloud
                        )
                    data = merge(data, {
                        assemblyState: assemblyState.state,
                        readyState,
                        ...other
                    })
                }
            } catch (e) {
                console.warn(e)
                error = "Application.Visualization.State.failure"
                data = { readyState: ReadyState.NotReady } // prevent inconsistent save data
            }
        }
        console.log("Generating additional save data finished", data, error)
        setBusyOverlay && setBusyOverlay(false)
        return error
            ? // eslint-disable-next-line prefer-promise-reject-errors
              Promise.reject({ message: error, data })
            : Promise.resolve(data)
    }

    useEffect(() => {
        if (
            !state.initialized ||
            !state.visualization ||
            !state.visualizationEnabled
        ) {
            return
        }
        const unsubscribes: Unsubscribes = []
        if (
            postMessage(ExternalMessages.SupportsSaveDataGeneration, eventBus)
        ) {
            const unregisterForExternalMessage = registerForExternalMessage(
                ExternalMessages.GenerateSaveData,
                (event) => {
                    generateSaveData()
                        .then((data) => {
                            let msg = ExternalMessages.SaveDataGenerated
                            if (data !== undefined) {
                                msg += JSON.stringify(data)
                            }
                            postMessage(msg, undefined, event.origin)
                        })
                        .catch((error) => {
                            console.warn(
                                "Save data was not (fully) generated.",
                                error
                            )
                            let msg =
                                ExternalMessages.SaveDataGenerationFailed +
                                L10n.format(error.message)
                            if (error.data) {
                                msg += "@@" + JSON.stringify(error.data)
                            }

                            postMessage(msg, undefined, event.origin)
                        })
                }
            )
            if (unregisterForExternalMessage) {
                unsubscribes.push(unregisterForExternalMessage)
            }
        }
        unsubscribes.push(subscribeSaveRequests(eventBus, generateSaveData))

        return () => unsubscribes.forEach((u) => u())
    }, [state])

    if (!state.visualizationEnabled) {
        return null
    }
    if (!state.initialized && !(loadState.failure || loadState.loading)) {
        return (
            <div
                key={BorderLayoutContent.VisualEditor}
                className={classNames.centered}
            >
                {ComponentFactory.instanceOf(
                    ComponentName.InitialLoadingSpinner,
                    {
                        show: true,
                        label: new URLSearchParams(window.location.search).get(
                            "product"
                        )
                    }
                )}
            </div>
        )
    }
    const noVisualization = () => {
        return (
            <div
                key={BorderLayoutContent.VisualEditor}
                className={classNames.centered}
            >
                <LoadVisualizationFailure
                    loadState={loadState}
                    triggerReload={() =>
                        setLoadState({ loading: true, failure: false })
                    }
                    classNames={classNames}
                />
            </div>
        )
    }

    const visualization = () => {
        return (
            <>
                {errorMessage &&
                    ComponentFactory.instanceOf(ComponentName.MessageBox, {
                        title: L10n.format("Error"),
                        onClick: () => setErrorMessage(null),
                        message: errorMessage
                    })}
                {ComponentFactory.instanceOf(ComponentName.ARDialog, {
                    eventBus
                })}
                {ComponentFactory.instanceOf(ComponentName.Visualization, {
                    key: BorderLayoutContent.VisualEditor,
                    visualEditorForwardRef: visualEditorRef,
                    settings: state.editorSettings,
                    eventBus,
                    locale: L10n.currentFullLocale(),
                    visualization: state.visualization,
                    ...delegatedProps,
                    styles: { root: { flex: 1 } }
                })}
            </>
        )
    }

    return state.visualization ? visualization() : noVisualization()
}

export const isVisualizationEnabled = (
    settings?: IVisualizationServiceSettings
) => {
    return (settings?.mode && settings.mode !== "none") || false
}

export const isVisualizationUnchanged = (appState: IAppState) => {
    const { visualization, node, settings } = appState
    if (visualization && node) {
        return (
            visualization.baseUrl === settings?.baseUrl &&
            visualization.cloud?.token === settings?.token &&
            node.configuration?.configurationId ===
                appState.configurationService?.configurationId
        )
    }
}

/**
 * Loads the visualization toolkit. Returns a Promise with {settings, configuration, visualization, visNode}
 * where the first ones are the arguments of this function.
 * The visualization field is an object of the visualization service instance.
 * The node is the communication object between the visual editor and the configurator.
 * Changes based on the configuration can be delegated to this node to change the contents of the visual editor.
 */
export const loadVisualization = async (
    appState: IAppState,
    visualizationState?: object
) => {
    if (
        !appState.settings ||
        !appState.visualizationEnabled ||
        isVisualizationUnchanged(appState)
    ) {
        return appState
    }
    const theme = appState.theme || getTheme()
    const visualization = await appState.visualizationProvider.load(
        appState.settings.baseUrl,
        appState.settings.version,
        typeof appState.settings.token === "function"
            ? appState.settings.token()
            : appState.settings.token
    )
    const configurationServerDef: object = {
        name: "default",
        baseUrl: appState.configurationSettings.baseUrl,
        jsessionid: appState.configurationSettings.jsessionid,
        token: appState.configurationSettings?.token,
        locale: L10n.currentFullLocale(),
        credentials: appState.configurationSettings?.credentials,
        options: {
            configuration: appState.configurationSettings?.settings,
            ...(appState.editorSettings.assembly?.catalogRootGroup && {
                rootGroup: appState.editorSettings.assembly.catalogRootGroup
            })
        }
    }

    visualization.cloud?.addServer(configurationServerDef)
    let node
    if (appState.configurationService) {
        const cfg = appState.configurationService.cfg as ICfg
        const visArguments = {
            id: cfg?.configuration?.articleName,
            configurationId: appState.configurationService?.configurationId
        }
        node = await visualization.cloud?.mount(
            "",
            "product",
            "article",
            visArguments,
            "default",
            true
        )
    } else if (visualizationState && Object.keys(visualizationState).length) {
        await visualization.cloud?.restore(visualizationState)
    }
    return {
        ...appState,
        initialized: true,
        settings: {
            ...appState.settings,
            theme
        },
        visualization,
        node
    } as IAppState
}

/**
 * subscribes to configuration changes to modify the visualization.
 */
export const subscribeConfigurationState = (
    eventBus: Bus<any>,
    appState: IAppState,
    reactConfigurator: typeof reactConfiguratorImport
) => {
    return eventBus
        .filter(
            (e: any) =>
                e.event === reactConfigurator.Constants.Events.InitialState ||
                e.event === reactConfigurator.Constants.Events.UpdateState
        )
        .onValue(() => {
            appState.node.update()
        })
}

const subscribeErrorMessages = (
    eventBus: Bus<any>,
    setErrorMessage: Dispatch<SetStateAction<any>>
) => {
    return eventBus
        .filter((e: any) => e.type === Events.ShowError)
        .onValue((e: any) => setErrorMessage(e.message))
}

const subscribeSaveRequests = (
    eventBus: Bus<any>,
    generateSaveData: () => Promise<ISaveData>
) => {
    return eventBus
        .filter((e: any) => e === VisualizationDataSaveEvents.SaveRequest)
        .onValue(() => {
            generateSaveData()
                .then((data) =>
                    eventBus.push({
                        event: VisualizationDataSaveEvents.SaveFinished,
                        data
                    } as ISaveResult)
                )
                .catch((error) =>
                    eventBus.push({
                        event: VisualizationDataSaveEvents.SaveIncomplete,
                        message: error.message,
                        data: error.data
                    } as ISaveResult)
                )
        })
}

const LoadVisualizationFailure = (props: {
    loadState: ILoadStatus
    triggerReload: () => void
    classNames: IProcessedStyleSet<IVisualizationWrapperStyles>
}) => {
    const { loadState, triggerReload, classNames } = props
    return loadState.loading && !loadState.failure ? (
        <Spinner
            size={SpinnerSize.large}
            label={L10n.format("Application.Visualization.reload")}
        />
    ) : (
        <Stack>
            <Stack.Item align="center">
                <Text block className={classNames.infoText}>
                    {L10n.format("Application.Visualization.load.failure")}
                </Text>
            </Stack.Item>
            <Stack.Item align="center">
                <ActionButton
                    text={L10n.format("Application.Visualization.load.retry")}
                    iconProps={{ iconName: SYNC }}
                    onClick={triggerReload}
                    className={classNames.retryButton}
                />
            </Stack.Item>
        </Stack>
    )
}

type Unsubscribes = (() => void)[]

interface ILoadStatus {
    loading: boolean
    failure: boolean
}

VisualizationWrapper.displayName = "VisualizationWrapper"
